import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Star, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface League {
  id: string;
  name: string;
  max_teams: number;
  created_at: string;
}

interface Team {
  id: string;
  user_id: string;
  league_id: string;
  owner_name?: string;
}

interface TeamPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  league: League | null;
  teams: Team[];
  onTeamSelect: (team: Team) => void;
  userTeam?: Team;
}

export function TeamPickerModal({
  isOpen,
  onClose,
  league,
  teams,
  onTeamSelect,
  userTeam,
}: TeamPickerModalProps) {
  if (!league) return null;

  const handleTeamSelect = (team: Team) => {
    onTeamSelect(team);
    onClose();
  };

  const getTeamDisplayName = (team: Team) => {
    return team.owner_name ? `${team.owner_name}'s Team` : "Team";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Choose a team in {league.name}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3">
          {userTeam && (
            <Card className="border-primary bg-primary/5">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-primary-foreground">
                      <Star className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-semibold">My Team</div>
                      <div className="text-sm text-muted-foreground">
                        Your squad in {league.name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="default" className="text-xs">
                      Owner
                    </Badge>
                    <Button 
                      size="sm"
                      onClick={() => handleTeamSelect(userTeam)}
                      className="bg-gradient-primary"
                    >
                      View Team
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {teams
            .filter(team => team.id !== userTeam?.id)
            .map((team) => (
              <Card key={team.id} className="hover:bg-accent/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted">
                        <Users className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="font-medium">
                          {getTeamDisplayName(team)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          View this team's lineup
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleTeamSelect(team)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Team
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

          {teams.length === 0 && (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No teams in this league yet</p>
              <Button 
                variant="link" 
                onClick={onClose}
                className="mt-2"
              >
                Return to league overview
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}